import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearState, selectAllSettings } from '../../../../modules/settings/store/slice';
import { doCreateSetting, doGetSettings, doUpdateSetting } from '../../../../modules/settings/store/thunk';
import { ROLES } from '../../../../constants';
import { AppDispatch } from '@/redux/store';

// Import shadcn components
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';

export const Channels = ({ locationId, companyId }: { locationId?: string; companyId?: string }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['EMAIL']);

  const existing = useSelector((state: any) => selectAllSettings(state));
  const userRole = useSelector((state: any) => state.auth.user.role);

  const objectProps = useMemo(
    () => ({
      companyId: companyId || undefined,
      locationId: locationId || undefined,
    }),
    [companyId, locationId],
  );

  useEffect(() => {
    if (userRole === ROLES.ADMIN || companyId) {
      dispatch(doGetSettings({ category: 'tnt-channels', ...objectProps }));
    }
    return () => {
      dispatch(clearState());
    };
  }, [companyId, locationId, dispatch, userRole, objectProps]);

  useEffect(() => {
    if (existing && existing.length > 0) {
      const emailSetting = existing.find((setting: any) => setting.keyword === 'EMAIL');
      const whatsappSetting = existing.find((setting: any) => setting.keyword === 'WHATSAPP');

      const channels: string[] = [];
      if (emailSetting?.value === 'true') channels.push('EMAIL');
      if (whatsappSetting?.value === 'true') channels.push('WHATSAPP');

      setSelectedChannels(channels);
    } else {
      setSelectedChannels(['EMAIL']); // default to email if no settings are found
    }
  }, [existing]);

  const toggleChannel = (channel: string) => {
    setSelectedChannels((prev) => (prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]));
  };

  const saveSettings = async () => {
    setIsSaving(true);

    // Find existing settings
    const emailSetting = existing?.find((e: any) => e.keyword === 'EMAIL') || null;
    const whatsappSetting = existing?.find((e: any) => e.keyword === 'WHATSAPP') || null;

    // Create settings data for EMAIL
    const emailSettingData = {
      id: emailSetting?.id || null,
      keyword: 'EMAIL',
      value: selectedChannels.includes('EMAIL') ? 'true' : 'false',
      group: 'TNT_CHANNELS',
      ...objectProps,
    };

    // Create settings data for WHATSAPP
    const whatsappSettingData = {
      id: whatsappSetting?.id || null,
      keyword: 'WHATSAPP',
      value: selectedChannels.includes('WHATSAPP') ? 'true' : 'false',
      group: 'TNT_CHANNELS',
      ...objectProps,
    };

    // Update or create EMAIL setting
    if (emailSettingData.id) {
      await dispatch(doUpdateSetting(emailSettingData));
    } else {
      await dispatch(doCreateSetting(emailSettingData));
    }

    // Update or create WHATSAPP setting
    if (whatsappSettingData.id) {
      await dispatch(doUpdateSetting(whatsappSettingData));
    } else {
      await dispatch(doCreateSetting(whatsappSettingData));
    }

    await dispatch(doGetSettings({ category: 'tnt-channels', ...objectProps }));
    setIsSaving(false);
  };

  // Check if current selection is different from saved one
  const hasChanges = () => {
    if (existing && existing.length > 0) {
      const emailSetting = existing.find((setting: any) => setting.keyword === 'EMAIL');
      const whatsappSetting = existing.find((setting: any) => setting.keyword === 'WHATSAPP');

      const emailEnabled = emailSetting?.value === 'true';
      const whatsappEnabled = whatsappSetting?.value === 'true';

      return (
        emailEnabled !== selectedChannels.includes('EMAIL') || whatsappEnabled !== selectedChannels.includes('WHATSAPP')
      );
    }
    return selectedChannels.length > 0;
  };

  if (!locationId && !companyId) {
    return <h4>Please select a company or location first</h4>;
  }

  return (
    <div className="space-y-4">
      <h4>Tracking Notification Channels</h4>

      <div className="w-full max-w-md">
        <div className="flex flex-col space-y-2 mb-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="email"
              checked={selectedChannels.includes('EMAIL')}
              onCheckedChange={() => toggleChannel('EMAIL')}
              className="mr-2"
            />
            <label htmlFor="email">Email</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="whatsapp"
              checked={selectedChannels.includes('WHATSAPP')}
              onCheckedChange={() => toggleChannel('WHATSAPP')}
              className="mr-2"
            />
            <label htmlFor="whatsapp">WhatsApp</label>
          </div>
        </div>
      </div>

      <div className="flex justify-start gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={saveSettings}
          disabled={isSaving || selectedChannels.length === 0 || !hasChanges()}
        >
          {isSaving ? (
            <>
              <span className="mr-1">Saving</span>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </>
          ) : (
            'Update Channels'
          )}
        </Button>
      </div>
    </div>
  );
};
